import isUndefined from 'lodash/isUndefined';
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';

import {
  BehaviorMeasurement,
  FUNNELS_MATH_TYPES,
  InsightsResourceType,
  MATH_TYPE,
  MetricType,
  NUMERIC_MATH_TYPES,
  PER_USER_AGGREGATIONS,
  PRIMITIVE_MATH_TYPES,
  RETENTION_MATH_TYPES,
  TopLevelMetricType,
  XAU_MATH_TYPES,
} from 'common/types/reports/bookmark';
import {isValidFormula} from 'common/util/formulas';

import {
  BehaviorMetric,
  BehaviorShowClause as BehaviorShowClauseType,
  FormulaMeasurement,
  FormulaReferencedMetrics,
  FormulaShowClause as FormulaShowClauseType,
  ShowClause as ShowClauseType,
} from '../util/types';
import {DATASET_MIXPANEL} from './constants';
import {FilterClause} from './filter-clause';
import {ClauseType} from './types';
import {getFormulaFromFormulaShowClause, isFormulaShowClause, sanitizeIfCustomProperty} from './utils';

export abstract class ShowClause {
  static TYPE: typeof ClauseType.Show = ClauseType.Show;
  public declare isHidden: boolean;
  public declare isExpanded: boolean;
  public declare hasUnsavedChanges: boolean;
  public declare type: TopLevelMetricType.Metric | TopLevelMetricType.Formula;

  public declare _idx: string | undefined;

  static createShowClause(attrs: ShowClauseType): ShowClauseClassType {
    if (isFormulaShowClause(attrs)) {
      return new FormulaShowClause(attrs);
    } else {
      return new BehaviorShowClause(attrs);
    }
  }

  abstract get attrs(): ShowClauseType;

  abstract get valid(): boolean;

  serialize(): ShowClauseType {
    return this.attrs;
  }

  abstract isRollingClause(): boolean;

  abstract isCumulativeClause(): boolean;

  abstract toBookmarkData(): ShowClauseType;

  abstract toUrlData(): ShowClauseType;
}

export type ShowClauseClassType = BehaviorShowClause | FormulaShowClause;

export class BehaviorShowClause extends ShowClause {
  declare id?: BehaviorMetric[`id`];
  declare name?: BehaviorMetric[`name`];
  declare behavior: BehaviorMetric[`behavior`];
  declare display?: BehaviorMetric[`display`];
  declare measurement: BehaviorMeasurement;
  type: TopLevelMetricType.Metric = TopLevelMetricType.Metric;

  constructor(attrs: BehaviorShowClauseType) {
    super();

    const {id, name, behavior, display, measurement} = attrs;

    /** unique identifier for each clause */
    this._idx = attrs._idx;

    this.id = id ?? undefined;
    this.name = name;
    this.measurement = measurement ?? ({} as BehaviorMeasurement);
    this.display = display;
    this.isHidden = attrs.isHidden === true;
    this.isExpanded = attrs.isExpanded === true;
    this.hasUnsavedChanges = attrs.hasUnsavedChanges === true;
    this.behavior = behavior ?? null;

    if (!behavior || !measurement) {
      return;
    }

    this.behavior = behavior;
    this.behavior.dataset = behavior.dataset || null;
    this.behavior.dataset = behavior.dataset === `mixpanel` ? DATASET_MIXPANEL : behavior.dataset;
    this.behavior.resourceType = behavior.resourceType || InsightsResourceType.Events;
    this.behavior.profileType = behavior.profileType || null;
    this.behavior.search = behavior.search || ``;
    this.behavior.dataGroupId = behavior.dataGroupId || null;

    this.behavior.type = behavior.type;

    if (this.behavior.resourceType === InsightsResourceType.People) {
      this.behavior.profileType = InsightsResourceType.People;
    }

    let defaultMathType: MATH_TYPE = PRIMITIVE_MATH_TYPES.TOTAL;
    if (
      this.behavior &&
      this.behavior.type &&
      [MetricType.Retention, MetricType.RetentionFrequency].includes(this.behavior?.type)
    ) {
      defaultMathType = RETENTION_MATH_TYPES.RETENTION_RATE;
    }
    this.measurement.math = measurement.math || defaultMathType;
    this.measurement.perUserAggregation = measurement.perUserAggregation || null;
    this.measurement.rolling = measurement.rolling || null;
    this.measurement.cumulative = measurement.cumulative || false;

    // TODO: Move sanitization logic app api bookmark endpoint
    this.measurement.property = measurement.property
      ? omit(sanitizeIfCustomProperty(measurement.property), `label`)
      : null;
  }

  serializeFilter() {
    if (this.behavior?.filters?.length) {
      const filters = this.behavior.filters.map((clause, index) => ({
        ...new FilterClause(clause).serialize(),
        // Assign determiner to the first filter
        ...(index === 0 ? {determiner: this.behavior?.filtersDeterminer} : {}),
      }));

      return {filters};
    } else {
      return {};
    }
  }

  getInlineCohort() {
    return this.behavior?.raw_cohort || null;
  }

  get isInlineCohort() {
    return !!this.getInlineCohort();
  }

  get attrs(): BehaviorShowClauseType {
    return {
      ...(this._idx ? {_idx: this._idx} : {}),
      ...(this.isHidden ? {isHidden: true} : {}),
      ...(this.isExpanded ? {isExpanded: true} : {}),
      behavior: {
        ...omitBy(
          {
            ...this.behavior,
            ...this.serializeFilter(),
          },
          isUndefined,
        ),
        resourceType: this.behavior?.resourceType,
      },
      display: omitBy(
        {
          ...this.display,
        },
        isUndefined,
      ) as BehaviorMetric[`display`],
      measurement: omitBy(
        {
          ...this.measurement,
        },
        isUndefined,
      ),
      type: TopLevelMetricType.Metric,
      ...(this.id ? {id: this.id} : {}),
    };
  }

  get customEventId() {
    if (this.behavior?.type === MetricType.CustomEvent) {
      return this.behavior.id;
    }
    return null;
  }

  get valid(): boolean {
    let isValidValue = true;

    if (this.behavior?.type === MetricType.CustomEvent) {
      isValidValue = !!this.behavior.id;
    }
    if (this.behavior?.type === MetricType.Cohort) {
      isValidValue = !!this.behavior.id || !!this.behavior.raw_cohort;
    }
    if (this.behavior?.type === MetricType.People) {
      isValidValue = !!this.behavior.name;
    }
    const isValidMathType = [
      ...Object.values(PRIMITIVE_MATH_TYPES),
      ...Object.values(NUMERIC_MATH_TYPES),
      ...Object.values(XAU_MATH_TYPES),
      ...Object.values(PER_USER_AGGREGATIONS),
      ...Object.values(FUNNELS_MATH_TYPES),
      ...Object.values(RETENTION_MATH_TYPES),
    ].includes(this.measurement.math!);
    return isValidValue && isValidMathType;
  }

  extend(attrs: Partial<BehaviorShowClauseType> = {}): BehaviorShowClause {
    const {behavior, measurement} = attrs;
    return new BehaviorShowClause({
      behavior: {
        ...this.behavior,
        ...omitBy(behavior, isUndefined),
        type: this.behavior?.type,
      },
      measurement: {
        ...this.measurement,
        ...omitBy(measurement, isUndefined),
      },
    });
  }

  toBookmarkData(): ShowClauseType {
    const attrs = this.attrs;
    return {
      ...(this._idx ? {_idx: this._idx} : {}),
      ...(this.id ? {id: this.id} : {}),
      ...(this.name ? {name: this.name} : {}),
      ...(this.isHidden ? {isHidden: true} : {}),
      ...(this.isExpanded ? {isExpanded: true} : {}),
      ...(this.hasUnsavedChanges ? {hasUnsavedChanges: true} : {}),
      behavior: {
        ...attrs.behavior,
      },
      display: {
        ...attrs.display,
      },
      measurement: attrs.measurement,
      type: TopLevelMetricType.Metric,
    };
  }

  isXauClause(): boolean {
    return Object.values(XAU_MATH_TYPES).includes(this.measurement.math as XAU_MATH_TYPES);
  }

  isCumulativeUniqueClause(): boolean {
    return this.measurement.math === PRIMITIVE_MATH_TYPES.CUMULATIVE_UNIQUE;
  }

  isRollingClause(): boolean {
    return !!this.measurement.rolling;
  }

  isCumulativeClause(): boolean {
    return !!this.measurement.cumulative;
  }

  toUrlData(): BehaviorShowClauseType {
    return omit(this.attrs, `_idx`);
  }
}

export class FormulaShowClause extends ShowClause {
  declare measurement: FormulaMeasurement;
  declare display: any;
  declare id?: number;
  declare definition?: string;
  declare name?: string;
  declare referencedMetrics?: FormulaReferencedMetrics;
  type: TopLevelMetricType.Formula = TopLevelMetricType.Formula;

  constructor(attrs: FormulaShowClauseType) {
    super();
    this.display = attrs.display;
    this.measurement = attrs.measurement || {};
    this.hasUnsavedChanges = attrs.hasUnsavedChanges ?? false;

    /** unique identifier for each clause */
    this._idx = attrs._idx;

    if (attrs.id) {
      this.id = attrs.id;
    }
    if (attrs.definition) {
      this.definition = attrs.definition;
    }
    if (attrs.name) {
      this.name = attrs.name;
    }
    if (attrs.referencedMetrics) {
      this.referencedMetrics = attrs.referencedMetrics;
    }

    if (attrs.isHidden === true) {
      this.isHidden = true;
    }

    if (attrs.isExpanded === true) {
      this.isExpanded = true;
    }
  }

  get attrs(): FormulaShowClauseType {
    return this.serializeFormula();
  }

  private serializeFormula(): FormulaShowClauseType {
    return {
      ...(this._idx ? {_idx: this._idx} : {}),
      ...(this.isHidden ? {isHidden: true} : {}),
      ...(this.isExpanded ? {isExpanded: true} : {}),
      ...(this.display ? {display: this.display} : {}),
      ...(this.hasUnsavedChanges ? {hasUnsavedChanges: true} : {}),
      definition: this.definition,
      id: this.id,
      measurement: {...this.measurement},
      name: this.name,
      referencedMetrics: this.referencedMetrics,
      type: TopLevelMetricType.Formula,
    };
  }

  get valid(): boolean {
    return isValidFormula(getFormulaFromFormulaShowClause(this));
  }

  isRollingClause(): boolean {
    return !!this.measurement.rolling;
  }

  isCumulativeClause(): boolean {
    return !!this.measurement.cumulative;
  }

  extend(attrs: Partial<FormulaShowClauseType> = {}): FormulaShowClause {
    return new FormulaShowClause({
      definition: this.definition,
      display: {
        ...this.display,
      },
      id: this.id,
      measurement: {
        ...this.measurement,
        ...attrs.measurement,
      },
      name: this.name,
      referencedMetrics: this.referencedMetrics,
      type: TopLevelMetricType.Formula,
    });
  }

  toBookmarkData(): FormulaShowClauseType {
    return this.serializeFormula();
  }

  toUrlData(): FormulaShowClauseType {
    return this.serializeFormula();
  }
}
